import { useCallback, useEffect, useState } from 'react';
import { useActiveContextOptional } from '@/auth/ActiveContextProvider';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types';
import { apiPlace, type Place } from '../place/place';

export type RoleCatalogEntry = components['schemas']['RoleCatalogEntryResponse'];

export type RoleCatalogState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly RoleCatalogEntry[] }
  | { readonly status: 'error'; readonly code: unknown; readonly httpStatus: number | undefined };

type Answer = {
  readonly data?: { readonly items: readonly RoleCatalogEntry[] } | undefined;
  readonly error?: unknown;
  readonly response: Response;
};

function readCatalog(
  placeKind: 'tenants' | 'organizations' | undefined,
  placeId: string | undefined,
  tenantId: string | undefined,
  organizationId: string | undefined,
): Promise<Answer> {
  if (placeKind !== undefined && placeId !== undefined) {
    return apiClient.GET('/v1/administration/{placeKind}/{placeId}/role-catalog', {
      params: { path: { placeKind, placeId } },
    });
  }
  if (tenantId === undefined || organizationId === undefined) {
    return Promise.resolve({
      error: { code: 'ACTIVE_CONTEXT_REQUIRED' },
      response: new Response(null, { status: 400 }),
    });
  }
  return apiClient.GET('/v1/administration/platform/role-catalog', {
    params: {
      header: { 'X-Active-Tenant-Id': tenantId, 'X-Active-Organization-Id': organizationId },
    },
  });
}

/**
 * Catalogo ruoli's one read (FR-AD-5): every role of the console, its module and scope, and which
 * tiers may assign it, in the server's order (the platform's own roles first, then by module,
 * each by name). Read only, for everyone who administers the place.
 *
 * The platform's route is evaluated in the caller's own active organization, so it carries its
 * headers, as the place chooser's does. Without an active organization there is nothing to
 * evaluate in, and the read answers as the server would (ACTIVE_CONTEXT_REQUIRED).
 */
export function useRoleCatalog(place: Place): {
  readonly state: RoleCatalogState;
  readonly reload: () => void;
} {
  const [state, setState] = useState<RoleCatalogState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);
  const active = useActiveContextOptional()?.active ?? null;
  const tenantId = active?.tenantId;
  const organizationId = active?.organizationId;
  const pair = apiPlace(place);
  const placeKind = pair?.placeKind;
  const placeId = pair?.placeId;

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    const read = readCatalog(placeKind, placeId, tenantId, organizationId);
    void read.then(
      ({ data, error, response }) => {
        if (cancelled) {
          return;
        }
        setState(
          data !== undefined && response.ok
            ? { status: 'ready', items: data.items }
            : { status: 'error', code: error, httpStatus: response.status },
        );
      },
      () => {
        if (!cancelled) {
          setState({ status: 'error', code: null, httpStatus: undefined });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [placeKind, placeId, tenantId, organizationId, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { state, reload };
}
